/// C:\Users\asten\Desktop\arttustenius.com\src\components\Landing\WebPricingLanding.jsx
import React, { useEffect, useMemo, useCallback, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link as ScrollLink } from "react-scroll";
import { Link as RouterLink } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import ThemedButton from "../ThemeButton/ThemedButton";
import PriceCalculator from "../PriceCalculator/PriceCalculator";
import {
  t as translate,
  normalizeLang,
  getLangFromPathname,
} from "../../i18n";
import { useSiteContent } from "../../content/SiteContentContext";

// --------------------------------------------------
// Kevyt FAQ-accordion (ei uusia riippuvuuksia)
// --------------------------------------------------
const FaqItem = ({ q, a, isOpen, onToggle }) => {
  return (
    <div className="rounded-2xl border border-black/10 bg-white/80 transition hover:-translate-y-[1px] hover:shadow-xl dark:border-white/10 dark:bg-white/5">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-5 py-4 focus:outline-none focus:ring-2 focus:ring-lime-400/60 rounded-2xl"
        aria-expanded={isOpen}
      >
        <div className="flex items-center justify-between gap-4">
          <span className="font-black text-base sm:text-lg text-black dark:text-white">{q}</span>
          <span
            className="
              shrink-0 px-3 py-1 rounded-full text-xs font-extrabold tracking-wide
              bg-black/5 dark:bg-white/10 border border-black/10 dark:border-white/10
              text-black/70 dark:text-white/70
            "
          >
            {isOpen ? "—" : "+"}
          </span>
        </div>
      </button>
      {isOpen ? (
        <div className="px-5 pb-5 mt-2">
          <p className="text-sm sm:text-base text-black/70 dark:text-white/75 leading-relaxed">{a}</p>
        </div>
      ) : null}
    </div>
  );
};

// --------------------------------------------------
// Pieni "badge chip"
// --------------------------------------------------
const Chip = ({ children }) => (
  <span
    className="
      px-3 py-1 rounded-full text-xs font-bold tracking-wide
      bg-white/60 dark:bg-white/10 border border-black/10 dark:border-white/10
      text-black/70 dark:text-white/80 backdrop-blur
    "
  >
    {children}
  </span>
);

// --------------------------------------------------
// Informaatio-/feature-kortti (sama look & feel)
// --------------------------------------------------
const InfoCard = ({ title, text, icon = "1", children }) => (
  <div
    className="
      rounded-2xl p-5 sm:p-6
      border border-black/10 dark:border-white/10
      bg-white/80 dark:bg-white/5
      transition hover:-translate-y-1 hover:shadow-xl
    "
  >
    <div className="flex items-start gap-3">
      <div
        className="
          mt-0.5 shrink-0 w-8 h-8 rounded-full
          bg-lime-400/20 border border-lime-400/30
          text-lime-700 dark:text-lime-300
          flex items-center justify-center font-black
        "
        aria-hidden="true"
      >
        {icon}
      </div>
      <div className="min-w-0">
        <h3 className="text-lg sm:text-xl font-black text-black dark:text-white">{title}</h3>
        <p className="mt-2 text-sm sm:text-base text-black/70 dark:text-white/75 leading-relaxed">
          {text}
        </p>
        {children ? <div className="mt-4">{children}</div> : null}
      </div>
    </div>
  </div>
);

// --------------------------------------------------
// Landeri
// --------------------------------------------------
const WebPricingLanding = ({ lang: langProp } = {}) => {
  useSiteContent();
  const reduceMotion = useReducedMotion();
  // AOS
  useEffect(() => {
    AOS.init({ duration: 900, easing: "ease-out-cubic", once: true });
  }, []);
  // App välittää normaalisti kielen propina.
  // Jos komponenttia käytetään ilman sitä, päätellään kieli URL:sta.
  const pathname =
    typeof window !== "undefined"
      ? window.location.pathname
      : "/";
  const lang =
    typeof langProp === "string" && langProp
      ? normalizeLang(langProp)
      : getLangFromPathname(pathname);
  // Sisäinen linkki yrityslanderille säilyttää nykyisen kieliversion.
  const businessLandingHref =
    lang === "fi"
      ? "/verkkosivut-yrityksille/"
      : `/${lang}/verkkosivut-yrityksille/`;
  const businessLandingCta =
    lang === "en"
      ? "Explore websites for businesses"
      : lang === "es"
        ? "Ver webs para empresas"
        : "Tutustu yrityksen verkkosivuihin";
  // Yhteinen i18n.js + nykyinen fallbackText-tuki.
  const t = useCallback(
    (key, vars, fallbackText) => {
      const result = translate(key, lang, vars);
      if (result === key && typeof fallbackText === "string") {
        return fallbackText;
      }
      return result;
    },
    [lang]
  );
  const customBuildText = t(
    "landingPricing.customBuildText",
    null,
    "Jokainen sivusto rakennetaan projektin mukaan – yritystä tai tekijää ei soviteta valmiiseen sivupohjaan."
  );
  // FAQ open state
  const [openFaq, setOpenFaq] = useState(0);
  const faqItems = useMemo(
    () => [
      {
        q: t(
          "landingPricing.sections.faq.items.0.q",
          null,
          "Sopiiko tämä myös yksityisille eikä vain yrityksille?"
        ),
        a: t(
          "landingPricing.sections.faq.items.0.a",
          null,
          "Kyllä. Teen sivuja myös yksityisille (esim. portfolio, artisti-/bändisivut, yhdistykset, tapahtumasivut). Hintalaskurissa WordPress-sivut on tarkoituksella muotoiltu niin, että se sopii sekä yritys- että yksityiskäyttöön."
        ),
      },
      {
        q: t("landingPricing.sections.faq.items.1.q", null, "Tarvitsenko hostingin teiltä?"),
        a: t(
          "landingPricing.sections.faq.items.1.a",
          null,
          "Et. Voit käyttää omaa hostingia. Tarvittaessa hosting ja tekninen ylläpito voidaan järjestää 360 €/vuosi."
        ),
      },
      {
        q: t("landingPricing.sections.faq.items.2.q", null, "Onko laskurin hinta lopullinen tarjous?"),
        a: t(
          "landingPricing.sections.faq.items.2.a",
          null,
          "Ei. Se on suuntaa-antava arvio. Lopullinen tarjous tehdään tavoitteen, sisällön, aikataulun ja mahdollisten integraatioiden perusteella."
        ),
      },
      {
        q: t("landingPricing.sections.faq.items.3.q", null, "Voinko käyttää omia tekstejä ja kuvia?"),
        a: t(
          "landingPricing.sections.faq.items.3.a",
          null,
          "Kyllä. Se usein myös pienentää projektin hintaa. Voin myös auttaa tekstien hiomisessa ja kuvien valinnassa / AI-kuvissa / kuvauksessa tarpeen mukaan."
        ),
      },
      {
        q: t("landingPricing.sections.faq.items.4.q", null, "Mitä tarkoittaa ylläpito?"),
        a: t(
          "landingPricing.sections.faq.items.4.a",
          null,
          "Tekninen ylläpito tarkoittaa päivityksiä, seurantaa, pieniä korjauksia ja toimivuuden varmistamista. Tavallisille verkkosivuille hosting ja tekninen ylläpito voidaan järjestää 360 €/vuosi. Räätälöityjen verkkopalveluiden ylläpito sovitaan erikseen."
        ),
      },
    ],
    [t]
  );
  // Esimerkkisisältö: mitä tasot tarkoittavat (selkeyttää "mitä tähän kuuluu?" -fiilistä)
  const tierCards = useMemo(
    () => [
      {
        title: t("landingPricing.tiers.starter.title", null, "Startti"),
        subtitle: t("landingPricing.tiers.starter.subtitle", null, "Nopea perusratkaisu"),
        bullets: [
          t("landingPricing.tiers.starter.b0", null, "Selkeä rakenne ja perussivut"),
          t("landingPricing.tiers.starter.b1", null, "Yhteydenotto / CTA näkyville"),
          t("landingPricing.tiers.starter.b2", null, "Perusoptimointi ja julkaisukunto"),
        ],
      },
      {
        title: t("landingPricing.tiers.business.title", null, "Business"),
        subtitle: t("landingPricing.tiers.business.subtitle", null, "Myyntiä tukeva kokonaisuus"),
        bullets: [
          t("landingPricing.tiers.business.b0", null, "Parempi sisältörakenne ja konversiopolku"),
          t("landingPricing.tiers.business.b1", null, "Brändiä tukeva toteutus"),
          t("landingPricing.tiers.business.b2", null, "Mittaus / SEO / lisäpalikat valinnan mukaan"),
        ],
      },
      {
        title: t("landingPricing.tiers.growth.title", null, "Kasvu / Laaja"),
        subtitle: t("landingPricing.tiers.growth.subtitle", null, "Räätälöidymmät tarpeet"),
        bullets: [
          t("landingPricing.tiers.growth.b0", null, "Laajempi rakenne tai toiminnallisuus"),
          t("landingPricing.tiers.growth.b1", null, "Integraatiot / automaatiot / mittaus"),
          t("landingPricing.tiers.growth.b2", null, "Kasvua tukeva tekninen kokonaisuus"),
        ],
      },
    ],
    [t]
  );
  // Kenelle sopii
  const audienceCards = useMemo(
    () => [
      {
        title: t("landingPricing.audience.0.title", null, "Yritykset"),
        text: t(
          "landingPricing.audience.0.text",
          null,
          "Palvelusivut, liidien keruu, yhteydenotot, löydettävyys ja selkeä myyntipolku."
        ),
      },
      {
        title: t("landingPricing.audience.1.title", null, "Yksityiset, artistit & tekijät"),
        text: t(
          "landingPricing.audience.1.text",
          null,
          "Portfolio, artisti- ja bändisivut, henkilöbrändi, tapahtumasivut ja esittelysivut."
        ),
      },
      {
        title: t("landingPricing.audience.2.title", null, "Yhdistykset & projektit"),
        text: t(
          "landingPricing.audience.2.text",
          null,
          "Selkeä infosivusto, yhteydenottolomake, ajantasainen sisältö ja helppo ylläpito."
        ),
      },
    ],
    [t]
  );
  return (
    <div className="mx-auto w-full max-w-7xl px-6 pb-24 pt-28 md:px-10 lg:px-16">
      <section
        id="web-pricing-landing"
        className="relative scroll-mt-24 overflow-hidden rounded-[2rem] border border-black/10 bg-white/70 px-6 py-10 shadow-[0_20px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_20px_80px_rgba(0,0,0,0.25)] md:px-10 md:py-12"
        aria-label={t("landingPricing.ariaLabel", null, "Verkkopalvelun hinta ja tarjous")}
      >
        {/* Taustan liikkuvat blur-blobit (sama fiilis kuin Worksissa) */}
        {!reduceMotion && (
          <>
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute -top-14 -left-14 w-80 h-80 rounded-full blur-3xl opacity-25 bg-lime-400/25"
              animate={{ x: [0, 55, 0], y: [0, 35, 0], scale: [1, 1.08, 1] }}
              transition={{
                duration: 70,
                repeat: Infinity,
                repeatType: "mirror",
                ease: "easeInOut",
              }}
            />
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-20 -right-14 w-96 h-96 rounded-full blur-3xl opacity-20 bg-sky-400/20"
              animate={{ x: [0, -65, 0], y: [0, -40, 0], scale: [1, 1.1, 1] }}
              transition={{
                duration: 85,
                repeat: Infinity,
                repeatType: "mirror",
                ease: "easeInOut",
              }}
            />
          </>
        )}
        {/* HERO */}
        <header className="max-w-5xl mx-auto text-center relative" data-aos="fade-up">
          <div className="inline-flex items-center mb-4 px-3 py-1 rounded-full border border-lime-400/25 bg-lime-400/10 text-lime-700 dark:text-lime-300 text-xs font-bold tracking-wide">
            <span>{t("landingPricing.hero.badge", null, "Selkeä arvio + nopea tarjous")}</span>
          </div>
          <h1 className="text-4xl font-black tracking-tight text-black dark:text-white md:text-5xl lg:text-6xl">
            {t(
              "landingPricing.hero.title",
              null,
              "Verkkosivujen hinta 2026 – mitä yrityksen verkkosivut maksavat?"
            )}
          </h1>
          <p className="mt-4 text-base md:text-lg text-black/70 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t(
              "landingPricing.hero.lead",
              null,
              "Katso mitä verkkosivut maksavat ja laske suuntaa-antava hinta yrityssivulle, artistisivulle, verkkokaupalle tai räätälöidylle verkkopalvelulle. Näet projektihinnan ja mahdolliset jatkuvat kulut erikseen."
            )}
          </p>
          <p className="mx-auto mt-4 max-w-3xl text-sm font-semibold leading-relaxed text-black/60 dark:text-white/60">
            {customBuildText}
          </p>
          {/* Hero CTA */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <ScrollLink
              to="landing-price-calculator"
              smooth
              duration={600}
              offset={-96}
              className="cursor-pointer"
            >
              <ThemedButton>
                {t("landingPricing.hero.primaryCta", null, "Laske suuntaa-antava hinta")} →
              </ThemedButton>
            </ScrollLink>
            <RouterLink
              to={lang === "fi" ? "/#contact" : `/${lang}/#contact`}
              className="
                 inline-flex items-center justify-center
                 px-5 py-3 rounded-full font-semibold cursor-pointer
                 border border-black/10 dark:border-white/10
                 bg-white/70 dark:bg-white/5 backdrop-blur
                 text-black dark:text-white
                 hover:-translate-y-[1px] transition-transform
               "
            >
              {t("landingPricing.hero.secondaryCta", null, "Pyydä tarjous")} →
            </RouterLink>
          </div>
          {/* Proof chips */}
          <div
            className="mt-6 flex flex-wrap items-center justify-center gap-2"
            data-aos="fade-up"
            data-aos-delay="120"
          >
            <Chip>{t("landingPricing.proof.0", null, "Yritykset + yksityiset")}</Chip>
            <Chip>{t("landingPricing.proof.1", null, "WordPress / WooCommerce / React")}</Chip>
            <Chip>{t("landingPricing.proof.2", null, "SEO + rakenne + mittaus")}</Chip>
            <Chip>{t("landingPricing.proof.3", null, "Selkeä tarjous ilman sitoumusta")}</Chip>
          </div>
        </header>
        {/* SUUNTAA-ANTAVAT HINTATASOT */}
        <section className="max-w-6xl mx-auto mt-12" data-aos="fade-up">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-black text-black dark:text-white">
              {t(
                "landingPricing.sections.priceRanges.title",
                null,
                "Mitä verkkosivut maksavat?"
              )}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-black/70 dark:text-white/75 leading-relaxed">
              {t(
                "landingPricing.sections.priceRanges.lead",
                null,
                "Suuntaa-antavia esimerkkejä verkkosivuprojektin hinnasta. Lopullinen hinta riippuu sivuston laajuudesta, sisällöistä ja tarvittavista ominaisuuksista."
              )}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-7">
            {["basic", "business", "growth"].map((key, index) => (
              <InfoCard
                key={key}
                icon={String(index + 1)}
                title={t(`landingPricing.sections.priceRanges.cards.${key}.title`)}
                text={t(`landingPricing.sections.priceRanges.cards.${key}.desc`)}
              >
                <p className="text-2xl font-black text-lime-700 dark:text-lime-300">
                  {t(`landingPricing.sections.priceRanges.cards.${key}.price`)}
                </p>
              </InfoCard>
            ))}
          </div>
          <p className="mt-5 text-center text-sm text-black/60 dark:text-white/60">
            {t(
              "landingPricing.sections.priceRanges.paymentNote",
              null,
              "Perus-SEO kuuluu verkkosivutoteutukseen. Projektin maksun voi sopia myös 1–3 erään."
            )}
          </p>
        </section>
        {/* MISTÄ VERKKOSIVUJEN HINTA MUODOSTUU */}
        <section className="max-w-6xl mx-auto mt-12" data-aos="fade-up">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-black text-black dark:text-white">
              {t(
                "landingPricing.sections.whatAffectsPrice.title",
                null,
                "Mistä verkkosivujen hinta muodostuu?"
              )}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-black/70 dark:text-white/75 leading-relaxed">
              {t(
                "landingPricing.sections.whatAffectsPrice.lead",
                null,
                "Verkkosivujen suunnittelun ja toteutuksen hintaan vaikuttavat ennen kaikkea sivuston laajuus, sisällöt ja tarvittavat ominaisuudet."
              )}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-7">
            {["0", "1", "2", "3", "4", "5"].map((key, index) => (
              <InfoCard
                key={key}
                icon={String(index + 1)}
                title={t(`landingPricing.sections.whatAffectsPrice.items.${key}.title`)}
                text={t(`landingPricing.sections.whatAffectsPrice.items.${key}.text`)}
              />
            ))}
          </div>
        </section>
        {/* KENELLE SOPII */}
        <section className="max-w-6xl mx-auto mt-12">
          <h2
            className="text-xl sm:text-2xl font-black text-center dark:text-white mb-6"
            data-aos="fade-up"
          >
            {t("landingPricing.audience.title", null, "Kenelle verkkosivut sopivat?")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {audienceCards.map((item, idx) => (
              <InfoCard
                key={item.title}
                title={item.title}
                text={item.text}
                icon={String(idx + 1)}
              />
            ))}
          </div>
          <div className="mt-6 text-center">
            <RouterLink
              to={businessLandingHref}
              className="inline-flex items-center font-bold text-black transition hover:translate-x-1 dark:text-white"
            >
              {businessLandingCta} →
            </RouterLink>
          </div>
        </section>
        {/* MITÄ TASOT TARKOITTAVAT */}
        <section className="max-w-6xl mx-auto mt-12" data-aos="fade-up">
          <div
            className="
              rounded-2xl p-6 sm:p-8
              border border-black/10 dark:border-white/10
              bg-white/80 dark:bg-white/5
            "
          >
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-xl sm:text-2xl font-black dark:text-white">
                {t("landingPricing.tiers.title", null, "Mitä tasot yleensä tarkoittavat?")}
              </h2>
              <p className="mt-3 text-sm sm:text-base text-black/70 dark:text-white/75 leading-relaxed">
                {t(
                  "landingPricing.tiers.lead",
                  null,
                  "Tämä auttaa hahmottamaan, mitä “Startti / Business / Kasvu” käytännössä tarkoittaa. Lopullinen sisältö riippuu projektin tavoitteesta ja valituista lisäpalikoista."
                )}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-7">
              {tierCards.map((card, idx) => (
                <div
                  key={card.title}
                  className="
                    rounded-2xl p-5
                    border border-black/10 dark:border-white/10
                    bg-gray-50/80 dark:bg-black/20
                  "
                  data-aos="fade-up"
                  data-aos-delay={idx * 90}
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-black text-black dark:text-white">{card.title}</h3>
                    <span
                      className="
                        text-[11px] px-2 py-1 rounded-full
                        bg-lime-400/15 border border-lime-400/25
                        text-lime-700 dark:text-lime-300 font-bold
                      "
                    >
                      {idx === 1
                        ? t("landingPricing.tiers.popular", null, "Usein valittu")
                        : t("landingPricing.tiers.example", null, "Esimerkki")}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-black/70 dark:text-white/70">{card.subtitle}</p>
                  <ul className="mt-4 space-y-2 text-sm text-black/75 dark:text-white/75">
                    {card.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <span
                          className="mt-[7px] inline-block w-2 h-2 rounded-full bg-lime-400/80"
                          aria-hidden="true"
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl p-4 border border-sky-400/20 bg-sky-400/10">
              <p className="text-sm sm:text-base text-black/80 dark:text-white/90">
                {t(
                  "landingPricing.tiers.note",
                  null,
                  "Perus-SEO kuuluu verkkosivutoteutukseen. Laajempi SEO, analytiikka, automaatiot, monikielisyys ja muut lisätoiminnot arvioidaan tarpeen mukaan. Tarvittaessa hosting ja tekninen ylläpito voidaan järjestää 360 €/vuosi."
                )}
              </p>
            </div>
          </div>
        </section>
        {/* MITÄ HINTAAN YLEENSÄ KUULUU / EI KUULU */}
        <section className="max-w-6xl mx-auto mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div data-aos="fade-right">
            <InfoCard
              title={t("landingPricing.includes.title", null, "Mitä hintaan yleensä kuuluu")}
              text={t(
                "landingPricing.includes.lead",
                null,
                "Riippuu valitusta tasosta ja palvelutyypistä, mutta tyypillisesti mukana on ainakin nämä:"
              )}
              icon="✓"
            >
              <ul className="space-y-2 text-sm sm:text-base text-black/75 dark:text-white/75">
                {[
                  t("landingPricing.includes.0", null, "Suunniteltu sivurakenne ja toteutus"),
                  t("landingPricing.includes.1", null, "Perus-CTA:t ja yhteydenottopolku"),
                  t("landingPricing.includes.2", null, "Responsiivisuus (mobiili / desktop)"),
                  t("landingPricing.includes.3", null, "Julkaisukunto + perusasetukset"),
                  t("landingPricing.includes.4", null, "Perus-SEO kuuluu verkkosivutoteutukseen"),
                ].map((row) => (
                  <li key={row} className="flex items-start gap-2">
                    <span className="mt-[7px] inline-block w-2 h-2 rounded-full bg-lime-400/80" />
                    <span>{row}</span>
                  </li>
                ))}
              </ul>
            </InfoCard>
          </div>
          <div data-aos="fade-left">
            <InfoCard
              title={t("landingPricing.notIncluded.title", null, "Mitä ei kuulu automaattisesti")}
              text={t(
                "landingPricing.notIncluded.lead",
                null,
                "Nämä voivat tulla mukaan, mutta ne arvioidaan tarpeen mukaan erikseen:"
              )}
              icon="!"
            >
              <ul className="space-y-2 text-sm sm:text-base text-black/75 dark:text-white/75">
                {[
                  t("landingPricing.notIncluded.0", null, "Domainin hankinta (osto yleensä erikseen)"),
                  t("landingPricing.notIncluded.1", null, "Laaja sisällöntuotanto / copy kaikille sivuille"),
                  t("landingPricing.notIncluded.2", null, "Erikoisintegraatiot (CRM, varausjärjestelmät, API:t)"),
                  t("landingPricing.notIncluded.3", null, "Jatkuva mainonnan hallinta"),
                  t("landingPricing.notIncluded.4", null, "Suuret muutostyöt projektin käynnistyttyä"),
                ].map((row) => (
                  <li key={row} className="flex items-start gap-2">
                    <span className="mt-[7px] inline-block w-2 h-2 rounded-full bg-sky-400/80" />
                    <span>{row}</span>
                  </li>
                ))}
              </ul>
            </InfoCard>
          </div>
        </section>
        {/* PROSESSI */}
        <section className="max-w-6xl mx-auto mt-12" data-aos="fade-up">
          <div
            className="
              rounded-2xl p-6 sm:p-8
              border border-black/10 dark:border-white/10
              bg-white/80 dark:bg-white/5
            "
          >
            <h2 className="text-xl sm:text-2xl font-black text-center dark:text-white">
              {t("landingPricing.process.title", null, "Miten homma etenee?")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-7">
              {[
                {
                  step: "1",
                  title: t("landingPricing.process.0.title", null, "Arvio"),
                  text: t(
                    "landingPricing.process.0.text",
                    null,
                    "Laske suuntaa-antava hinta ja hahmota sopiva taso."
                  ),
                },
                {
                  step: "2",
                  title: t("landingPricing.process.1.title", null, "Yhteydenotto"),
                  text: t(
                    "landingPricing.process.1.text",
                    null,
                    "Kerro tavoite, aikataulu ja mitä haluat sivun tekevän."
                  ),
                },
                {
                  step: "3",
                  title: t("landingPricing.process.2.title", null, "Tarkennus"),
                  text: t(
                    "landingPricing.process.2.text",
                    null,
                    "Käydään läpi sisältö, laajuus ja valitaan järkevä toteutus."
                  ),
                },
                {
                  step: "4",
                  title: t("landingPricing.process.3.title", null, "Tarjous"),
                  text: t(
                    "landingPricing.process.3.text",
                    null,
                    "Saat selkeän tarjouksen ja seuraavat askeleet ilman sitoumusta."
                  ),
                },
              ].map((item) => (
                <div
                  key={item.step}
                  className="rounded-2xl p-4 border border-black/10 dark:border-white/10 bg-gray-50/80 dark:bg-black/20"
                >
                  <div className="w-9 h-9 rounded-full bg-lime-400/20 border border-lime-400/30 text-lime-700 dark:text-lime-300 font-black flex items-center justify-center">
                    {item.step}
                  </div>
                  <h3 className="mt-3 font-black text-black dark:text-white">{item.title}</h3>
                  <p className="mt-2 text-sm text-black/70 dark:text-white/70 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* HINTALASKURI */}
        <section id="landing-price-calculator" className="max-w-7xl mx-auto mt-12 scroll-mt-24">
          <div
            className="
              rounded-2xl
              p-5 sm:p-8
            "
            data-aos="fade-up"
          >
            <div className="text-center max-w-3xl mx-auto mb-6">
              <h2 className="text-2xl sm:text-3xl font-black dark:text-white">
                {t("landingPricing.calculator.title", null, "Laske suuntaa-antava hinta")}
              </h2>
              <p className="mt-3 text-sm sm:text-base text-black/70 dark:text-white/70">
                {t(
                  "landingPricing.calculator.lead",
                  null,
                  "Valitse palvelutyyppi, projektin taso ja lisäpalikat. Laskuri antaa realistisen suuntaa-antavan arvion projektihinnasta ja jatkuvista kuluista ennen yhteydenottoa."
                )}
              </p>
            </div>
            {/* Käytetään sun nykyistä laskuria */}
            <PriceCalculator />
          </div>
        </section>
        {/* FAQ */}
        <section className="max-w-5xl mx-auto mt-12" data-aos="fade-up">
          <h2 className="text-xl sm:text-2xl font-black text-center dark:text-white mb-6">
            {t("landingPricing.sections.faq.title", null, "Usein kysyttyä")}
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, idx) => (
              <FaqItem
                key={item.q}
                q={item.q}
                a={item.a}
                isOpen={openFaq === idx}
                onToggle={() => setOpenFaq((prev) => (prev === idx ? -1 : idx))}
              />
            ))}
          </div>
        </section>
        {/* LOPPU-CTA */}
        <section className="max-w-5xl mx-auto mt-12" data-aos="fade-up">
          <div
            className="
              rounded-2xl p-6 sm:p-8 text-center
              border border-lime-500/30 bg-lime-500/5 dark:bg-lime-500/10
            "
          >
            <h2 className="text-2xl sm:text-3xl font-black text-black dark:text-white">
              {t("landingPricing.sections.finalCta.title", null, "Haluatko tarkan tarjouksen sivuista?")}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-black/70 dark:text-white/75 max-w-3xl mx-auto leading-relaxed">
              {t(
                "landingPricing.sections.finalCta.lead",
                null,
                "Lähetä lyhyt kuvaus projektista tai laske ensin suuntaa-antava hinta. Saat selkeän ehdotuksen siitä, mitä sivulla kannattaa tehdä ensin, mitä mitata ja miten yhteydenottoja voidaan lisätä."
              )}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <ThemedButton
                onClick={() => {
                  window.location.href =
                    lang === "fi" ? "/#contact" : `/${lang}/#contact`;
                }}
              >
                {t("landingPricing.sections.finalCta.button", null, "Siirry yhteydenottoon")} →
              </ThemedButton>
              <a
                href={lang === "fi" ? "/#works" : `/${lang}/#works`}
                className="
                  inline-flex items-center justify-center
                  px-5 py-3 rounded-full font-semibold cursor-pointer
                  border border-black/10 dark:border-white/10
                  bg-white/70 dark:bg-white/5 backdrop-blur
                  text-black dark:text-white
                  hover:-translate-y-[1px] transition-transform
                "
              >
                {t("landingPricing.cta.secondary", null, "Katso portfolio")} →
              </a>
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm">
              <Chip>{t("landingPricing.cta.proof0", null, "✅ Nopea vastaus")}</Chip>
              <Chip>{t("landingPricing.cta.proof1", null, "✅ Ei sitoumusta")}</Chip>
              <Chip>{t("landingPricing.cta.proof2", null, "✅ Selkeä seuraava askel")}</Chip>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
};
export default WebPricingLanding;